/** Represents the supported runtime types without coercing strict comparison values. */
export type ConditionValueType = 'text' | 'number' | 'boolean' | 'date' | 'variable';

/** Describes one comparison or a recursively nested logical group. */
export interface TemplateCondition {
  kind: 'rule' | 'group';
  operator: string;
  path?: string;
  value?: string;
  valueType?: ConditionValueType;
  children?: TemplateCondition[];
}

/** Restricts authored paths to the same safe syntax as the variable picker. */
const PATH = /^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_-]*)*$/;
export const CONDITION_OPERATORS = {
  truthy: 'ist wahr', present: 'ist ausgefüllt', empty: 'ist nicht ausgefüllt',
  eq: 'ist gleich', ne: 'ist ungleich', gt: 'ist größer als', gte: 'ist mindestens', lt: 'ist kleiner als', lte: 'ist höchstens'
};

/** Creates an empty row whose missing variable must be selected before saving. */
export function newCondition(): TemplateCondition {
  return {kind: 'rule', operator: 'present', path: '', value: '', valueType: 'text'};
}

/** Validates every row before it can become executable Handlebars. */
export function validCondition(condition: TemplateCondition): boolean {
  if (condition.kind === 'group') {
    return ['and', 'or'].indexOf(condition.operator) !== -1 && !!condition.children?.length &&
      condition.children.every(validCondition);
  }
  if (!PATH.test(condition.path || '') || !CONDITION_OPERATORS[condition.operator]) {
    return false;
  }
  if (['truthy', 'present', 'empty'].indexOf(condition.operator) !== -1) {
    return true;
  }
  if (condition.valueType === 'variable') {
    return PATH.test(condition.value || '');
  }
  if (condition.valueType === 'number') {
    const value = (condition.value || '').replace(',', '.');
    return /^-?\d+(\.\d+)?$/.test(value) && Number.isFinite(Number(value));
  }
  if (condition.valueType === 'boolean') {
    return condition.value === 'true' || condition.value === 'false';
  }
  if (condition.valueType === 'date') {
    const value = condition.value || '';
    const date = new Date(value);
    return /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }
  return condition.valueType === 'text' && typeof condition.value === 'string' &&
    !/[{}<>&\\\r\n]/.test(condition.value);
}

/** Generates only helpers registered by both the API and the fill application. */
export function conditionExpression(condition: TemplateCondition): string {
  if (!validCondition(condition)) {
    return '';
  }
  if (condition.kind === 'group') {
    return `(${condition.operator} ${condition.children.map(conditionExpression).join(' ')})`;
  }
  const path = condition.path;
  if (condition.operator === 'truthy') { return path; }
  if (condition.operator === 'present') {
    return `(and (ne ${path} null) (ne ${path} undefined) (ne ${path} ""))`;
  }
  if (condition.operator === 'empty') {
    return `(or (eq ${path} null) (eq ${path} undefined) (eq ${path} ""))`;
  }
  const value = condition.valueType === 'text' || condition.valueType === 'date'
    ? JSON.stringify(condition.value) : condition.valueType === 'number' ? condition.value.replace(',', '.') : condition.value;
  return `(${condition.operator} ${path} ${value})`;
}

/** Parses a deliberately limited grammar and leaves all unsupported source expressions untouched. */
export function parseCondition(expression: string): TemplateCondition | null {
  const tokens = expression.match(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\(|\)|[^\s()]+/g) || [];
  let cursor = 0;
  /** Reads a helper subexpression or a plain truthiness check. */
  const read = (): TemplateCondition => {
    const first = tokens[cursor++];
    if (first !== '(') {
      if (!PATH.test(first || '') || ['true', 'false', 'null', 'undefined'].indexOf(first) !== -1) { return null; }
      return {kind: 'rule', operator: 'truthy', path: first, valueType: 'text', value: ''};
    }
    const helper = tokens[cursor++];
    if (helper === 'and' || helper === 'or') {
      const children: TemplateCondition[] = [];
      while (cursor < tokens.length && tokens[cursor] !== ')') {
        const child = read();
        if (!child) { return null; }
        children.push(child);
      }
      if (tokens[cursor++] !== ')' || !children.length) { return null; }
      return simplifyPresence({kind: 'group', operator: helper, children});
    }
    if (['eq', 'ne', 'gt', 'gte', 'lt', 'lte'].indexOf(helper) === -1) { return null; }
    const path = tokens[cursor++];
    const literal = tokens[cursor++];
    if (!PATH.test(path || '') || !literal || tokens[cursor++] !== ')') { return null; }
    let valueType: ConditionValueType = 'variable';
    let value = literal;
    if (literal[0] === '"') {
      try { value = JSON.parse(literal); } catch { return null; }
      valueType = /^\d{4}-\d{2}-\d{2}$/.test(value) ? 'date' : 'text';
    } else if (literal[0] === "'") {
      value = literal.slice(1, -1).replace(/\\'/g, "'").replace(/\\\\/g, '\\');
      valueType = 'text';
    } else if (literal === 'true' || literal === 'false') {
      valueType = 'boolean';
    } else if (/^-?\d+(\.\d+)?$/.test(literal)) {
      valueType = 'number';
    }
    return {kind: 'rule', operator: helper, path, valueType, value};
  };
  const condition = read();
  if (!condition || cursor !== tokens.length) { return null; }
  return validCondition(condition) ? condition : null;
}

/** Collapses generated presence checks at every nesting depth into one readable rule. */
function simplifyPresence(condition: TemplateCondition): TemplateCondition {
  if (condition.kind === 'group' && condition.children.length === 3) {
    const children = condition.children;
    const operator = condition.operator === 'and' ? 'ne' : 'eq';
    if (children.every(child => child.kind === 'rule' && child.path === children[0].path && child.operator === operator) &&
      children[0].valueType === 'variable' && children[0].value === 'null' &&
      children[1].valueType === 'variable' && children[1].value === 'undefined' &&
      children[2].valueType === 'text' && children[2].value === '') {
      return {...newCondition(), path: children[0].path, operator: condition.operator === 'and' ? 'present' : 'empty'};
    }
  }
  return condition;
}

/** Builds a readable summary using the same variable names as the picker. */
export function conditionLabel(condition: TemplateCondition, labels: {[path: string]: string}, translate = (text: string) => text): string {
  if (condition.kind === 'group') {
    return '(' + condition.children.map(child => conditionLabel(child, labels, translate))
      .join(` ${translate(condition.operator === 'and' ? 'UND' : 'ODER')} `) + ')';
  }
  const label = labels[(condition.path || '').replace(/^patient\./, 'client.')] || condition.path || translate('Variable wählen');
  const comparison = translate(CONDITION_OPERATORS[condition.operator]);
  if (['truthy', 'present', 'empty'].indexOf(condition.operator) !== -1) { return `${label} ${comparison}`; }
  const value = condition.valueType === 'variable' ? labels[(condition.value || '').replace(/^patient\./, 'client.')] || condition.value :
    condition.valueType === 'boolean' ? translate(condition.value === 'true' ? 'Ja' : 'Nein') : condition.value;
  return `${label} ${comparison} ${condition.valueType === 'text' ? '„' + value + '“' : value}`;
}
